import React from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { Smartphone, MapPin, Clock, Zap, Globe } from 'lucide-react';
import kioskImage from '../../assets/ai_health_kiosk_1769984548240.png';

const FutureExpansionSection: React.FC = () => {
  const kioskFeatures = [
    {
      icon: Clock,
      title: '24/7 Access',
      description: 'Round-the-clock health screening without appointments'
    },
    {
      icon: Zap,
      title: 'Instant Results',
      description: 'AI-powered analysis delivers insights in under 5 minutes'
    },
    {
      icon: MapPin,
      title: 'High-Traffic Locations',
      description: 'Malls, transit hubs, gyms, and corporate campuses'
    },
    {
      icon: Smartphone,
      title: 'Seamless Integration',
      description: 'Results sync directly to your BIOTUNE Health OS dashboard'
    }
  ];



  return (
    <section className="py-32 bg-gradient-to-br from-theme-bg via-theme-surface to-theme-bg relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-theme-primary/5 blur-[150px] rounded-full" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-theme-secondary/5 blur-[150px] rounded-full" />

      <Container className="relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-theme-primary/10 border border-theme-primary/20 text-theme-primary text-xs font-bold uppercase tracking-widest mb-6">
            <Globe className="w-4 h-4" />
            <span>Future Expansion 2027-2028</span>
          </div>
          <h2 className="text-5xl font-black text-theme-text-primary mb-6">
            AI Health Kiosks: <br />
            <span className="gradient-heading">The Next Frontier.</span>
          </h2>
          <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto leading-relaxed">
            Inspired by Shanghai's revolutionary deployment of 250+ autonomous health screening kiosks, 
            BIOTUNE is planning to bring 24/7 accessible biomarker testing to high-traffic locations worldwide.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Kiosk Visual */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-theme-primary/20 to-theme-secondary/20 blur-2xl rounded-3xl" />
            <img
              src={kioskImage}
              alt="AI Health Screening Kiosk"
              className="relative rounded-3xl shadow-2xl border border-theme-border"
            />
          </motion.div>

          {/* Features Grid */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-theme-text-primary mb-8">Autonomous Health Screening</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {kioskFeatures.map((feature, idx) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-6 rounded-2xl bg-theme-surface border border-theme-border hover:bg-theme-elevated transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-theme-primary/20 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-theme-primary" />
                  </div>
                  <h4 className="text-lg font-bold text-theme-text-primary mb-2">{feature.title}</h4>
                  <p className="text-sm text-theme-text-secondary">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default FutureExpansionSection;
